import type { ShiftCell, ShiftSignal, ShiftSlice } from "../api/types";

/**
 * Разбор сводки теневой покупки смещённого края.
 *
 * # Что здесь считается и почему именно здесь
 *
 * Сервер шлёт числители со знаменателями и суммы деньгами; доли считаются тут.
 * Так знаменатель всегда рядом с долей: клетка на трёх строках обязана выглядеть
 * иначе, чем клетка на трёхстах, а посчитанная на сервере доля приезжает уже без
 * него.
 */

/** Порог из базисных пунктов в доллары: 6300 → 0.63. */
export function thresholdOf(bp: number): number {
  return bp / 10_000;
}

/** Подпись ступени лесенки. */
export function thresholdLabel(bp: number): string {
  return thresholdOf(bp).toFixed(2);
}

/**
 * Деньги из строки контракта.
 *
 * Пустая строка и мусор дают null, а НЕ ноль: «не наблюдали» и «ноль» на экране
 * обязаны различаться, и подставленный ноль молча вошёл бы в среднее.
 */
export function amount(raw: string | undefined | null): number | null {
  if (raw === undefined || raw === null || raw === "") return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

/** Доля с прочерком вместо деления на ноль. */
export function rate(n: number, total: number): number | null {
  if (total <= 0) return null;
  return n / total;
}

/** Среднее денег по знаменателю. */
export function mean(sum: string, n: number): number | null {
  if (n <= 0) return null;
  const v = amount(sum);
  return v === null ? null : v / n;
}

/**
 * Базовая линия по минуте окна для ПЯТИМИНУТКИ, в долях.
 *
 * Измерено 2026-08-26 на 370 окнах и 580 108 публичных сделках: по одному знаку
 * «цена против страйка» известно 64–65 % на первой минуте, 69–70 % на второй,
 * 80 % на третьей, 91–92 % на четвёртой. Это планка, которую обязан побивать
 * ЛЮБОЙ признак, и ни один проверенный её не побил.
 *
 * Хранится здесь, а не приезжает с сервера, ровно потому, что это ЗАМЕР, а не
 * текущее состояние: он не пересчитывается сам собой и меняется только новым
 * замером, который придёт вместе с правкой этой строки.
 */
const BASELINE_5M: Record<number, number> = {
  1: 0.645,
  2: 0.695,
  3: 0.8,
  4: 0.915,
};

/**
 * Базовая линия для клетки. null означает «замера для такого окна нет».
 *
 * Пятнадцатиминутка сюда не попадает намеренно: замер сделан на пятиминутках, и
 * приписать его чужой длительности значило бы сравнить сигнал с планкой, которую
 * никто не мерил.
 */
export function baseline(cell: ShiftCell): number | null {
  if (cell.window_len_s !== 300) return null;
  const v = BASELINE_5M[cell.minute];
  return v === undefined ? null : v;
}

/** Разобранная клетка: всё, что панель показывает по одной строке. */
export interface ShiftView {
  cell: ShiftCell;
  /** Доля угаданного по всем строкам и по строкам без разворота. */
  winRate: number | null;
  firstWinRate: number | null;
  /** Две линии сравнения: цена налива и вероятность физики. */
  priceLine: number | null;
  physLine: number | null;
  /** Кромка против каждой линии, в долях (не в процентных пунктах). */
  edgeVsPrice: number | null;
  edgeVsPhys: number | null;
  /** Кромка против измеренной базовой линии; null у окон, где её не мерили. */
  edgeVsBaseline: number | null;
  /** Доля строк, у которых план по порогу был исполним. */
  limitShare: number | null;
  /** Доля откатов от наблюдённых до конца окна. */
  retracedShare: number | null;
  pnl: number | null;
}

/** view раскладывает клетку в то, что читает панель. */
export function view(cell: ShiftCell): ShiftView {
  const winRate = rate(cell.won, cell.settled);
  const priceLine = mean(cell.price_sum, cell.priced);
  const physLine = mean(cell.phys_sum, cell.phys_n);
  const base = baseline(cell);
  return {
    cell,
    winRate,
    firstWinRate: rate(cell.first_won, cell.first_settled),
    priceLine,
    physLine,
    // Кромка считается только когда есть ОБА конца: доля и линия. Ноль вместо
    // прочерка читался бы как «кромки нет», а правда — «сравнивать не с чем».
    // Деньги приезжают ДЕСЯТИЧНОЙ строкой ("0.646"), а не микро-единицами:
    // money.Amount.String() печатает доллары. Делить их ещё на масштаб значило
    // бы получить кромку в миллион раз больше — и она выглядела бы огромной
    // ровно там, где её нет.
    edgeVsPrice: winRate !== null && priceLine !== null ? winRate - priceLine : null,
    edgeVsPhys: winRate !== null && physLine !== null ? winRate - physLine : null,
    edgeVsBaseline: winRate !== null && base !== null ? winRate - base : null,
    limitShare: rate(cell.limit_ok, cell.signals),
    retracedShare: rate(cell.retraced, cell.watched),
    pnl: amount(cell.pnl),
  };
}

/**
 * Порядок клеток на экране: длительность, затем ступень, затем минута.
 *
 * Минута ВНУТРИ ступени, а не наоборот: вопрос звучит «на какой минуте эта цена
 * хороша», и разложенная иначе таблица отвечала бы на другой.
 */
export function order(cells: ShiftCell[]): ShiftCell[] {
  return [...cells].sort((a, b) => {
    if (a.window_len_s !== b.window_len_s) return a.window_len_s - b.window_len_s;
    if (a.threshold_bp !== b.threshold_bp) return a.threshold_bp - b.threshold_bp;
    return a.minute - b.minute;
  });
}

/**
 * Ключ клетки — тот же, каким сервер собирает форму счёта.
 *
 * Режима съёма в нём больше нет: сводка читается только по строкам, снятым по
 * стакану, — строки секундного обхода мерили наше запаздывание, а не рынок, и
 * их сравнение с быстрым съёмом своё отработало (цена запаздывания измерена).
 */
export function cellKey(cell: ShiftCell): string {
  return `${cell.window_len_s}-${cell.threshold_bp}-${cell.minute}`;
}

/**
 * Корзины разреза, лежащие под этой клеткой.
 *
 * Порядок — РАЗВОРОТ снаружи, подтверждение внутри, и это не вкус. Вопрос
 * разреза звучит «помогает ли подтверждение модели», а подтверждённые входы на
 * три четверти лежат в развернувшихся окнах: положи подтверждение снаружи, и
 * рядом встали бы строки с разной базовой линией, то есть сравнивалась бы не та
 * пара. Так соседние строки отличаются РОВНО одним признаком.
 */
export function slicesFor(slices: ShiftSlice[], cell: ShiftCell): ShiftSlice[] {
  return slices
    .filter(
      (s) =>
        s.window_len_s === cell.window_len_s &&
        s.threshold_bp === cell.threshold_bp &&
        s.minute === cell.minute,
    )
    .sort((a, b) => {
      if (a.reversal !== b.reversal) return Number(a.reversal) - Number(b.reversal);
      return Number(b.confirmed) - Number(a.confirmed);
    });
}

/** Ключ корзины: клетка плюс оба признака. */
export function sliceKey(s: ShiftSlice): string {
  return `${s.window_len_s}-${s.threshold_bp}-${s.minute}-${s.confirmed}-${s.reversal}`;
}

/**
 * Подпись корзины.
 *
 * «Модель молчала», а не «не подтвердила»: подтверждения нет и когда подсистема
 * расхождения стояла в `off`, и когда порог оказался выше рынка. Разрез эти
 * случаи не различает, и обещать различие подписью значило бы врать о
 * знаменателе.
 */
export function sliceLabel(s: ShiftSlice): string {
  const ход = s.reversal ? "после разворота" : "без разворота";
  return `${ход} · ${s.confirmed ? "подтверждено" : "модель молчала"}`;
}

/** Разобранная корзина: те же три вопроса, что у строки сводки. */
export interface ShiftSliceView {
  slice: ShiftSlice;
  winRate: number | null;
  /** Линия цены КОРЗИНЫ: у подтверждённых входов налив дешевле. */
  priceLine: number | null;
  edgeVsPrice: number | null;
  pnl: number | null;
}

/**
 * sliceView раскладывает корзину.
 *
 * Кромка считается против СВОЕЙ линии цены, а не против линии клетки. Замер
 * 2026-09-01: подтверждённые входы наливаются по 0.591, неподтверждённые по
 * 0.629, и общая планка перевернула бы знак у обеих корзин.
 */
export function sliceView(s: ShiftSlice): ShiftSliceView {
  const winRate = rate(s.won, s.settled);
  const priceLine = mean(s.price_sum, s.priced);
  return {
    slice: s,
    winRate,
    priceLine,
    edgeVsPrice: winRate !== null && priceLine !== null ? winRate - priceLine : null,
    pnl: amount(s.pnl),
  };
}

/** Ступени лесенки, встреченные в сводке. */
export function thresholds(cells: ShiftCell[]): number[] {
  return [...new Set(cells.map((c) => c.threshold_bp))].sort((a, b) => a - b);
}

/**
 * Хватает ли строк, чтобы вообще читать долю.
 *
 * Двадцать — то же число и тот же довод, что у вердикта копирования: на меньшей
 * выборке разница в десять процентных пунктов не отличается от монетки, и
 * показывать её кромкой значило бы звать оператора за шумом.
 */
export const MIN_SETTLED = 20;

export function trustworthy(cell: ShiftCell): boolean {
  return cell.settled >= MIN_SETTLED;
}

/**
 * Цена налива по плану «по рынку»: деньги, делённые на размер.
 *
 * Не `price`, и это не мелочь: у тейкера лимитная цена — ПОТОЛОК, а исполнение
 * идёт по встречным уровням, и улучшение биржа отдаёт ему. Считать по потолку
 * значило бы завышать налив на каждой строке.
 *
 * null означает «плана не было», а не ноль.
 */
export function fillPrice(s: ShiftSignal): number | null {
  const quote = amount(s.market.quote);
  const size = amount(s.market.size);
  if (quote === null || size === null || size <= 0) return null;
  return quote / size;
}

/**
 * Перелёт цены налива над порогом — ГЛАВНОЕ число этой ленты.
 *
 * Пока обход шёл раз в секунду, вход по ступени 0.55 сплошь и рядом наливался по
 * 0.66: цена успевала уехать за такт. Знак всего замера определялся этим
 * перелётом, а не предсказанием — та же доля угаданного убыточна на наливе 0.66
 * и прибыльна на 0.56.
 *
 * null означает «плана не было». Отрицательный перелёт законен: стакан отдал
 * уровень дешевле порога.
 */
export function overshoot(s: ShiftSignal): number | null {
  const fill = fillPrice(s);
  if (fill === null) return null;
  return fill - thresholdOf(s.threshold_bp);
}

/** Подпись строки ленты: сторона, ступень и минута. */
export function signalLabel(s: ShiftSignal): string {
  const minute = minuteOf(s);
  const side = s.outcome_name ?? s.asset_id.slice(0, 8);
  return `${side} · ${thresholdLabel(s.threshold_bp)} · м${minute}`;
}

/**
 * Минута окна строки ленты.
 *
 * Повторяет domain.ShiftSignal.Minute и выражение сводки в SQL; третье место, и
 * дублирование намеренно — лента приезжает строками, а не клетками. Разъедься
 * счёт, строка ленты попала бы в чужую клетку, и обе выглядели бы правдоподобно.
 */
export function minuteOf(s: ShiftSignal): number {
  if (s.window_len_s <= 0) return 0;
  const elapsed = Math.max(0, s.window_len_s * 1000 - s.lead_ms);
  const last = Math.floor((s.window_len_s - 1) / 60) + 1;
  return Math.min(Math.floor(elapsed / 60_000) + 1, last);
}
